import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Build as BuildIcon,
} from "@mui/icons-material";
import serviceService from "../services/serviceService";
import tenantService from "../services/tenantService";
import categoryService from "../services/categoryService";
import { darkColors, lightColors } from "../theme";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";
import { createCommonStyles } from "../theme/styles";
import {
  colors,
  getStatusColors,
  getTableHeaderStyles,
  getActualTableRowBackground,
} from "../theme/colors";
import Pagination from "../components/Pagination";
import { isTenantAdmin as isTenantAdminHelper } from "../utils/userHelper";
import { toast } from "react-toastify";

const Services = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const theme = useTheme();
  const { isDarkMode } = useAppTheme();
  const customColors = isDarkMode ? darkColors : lightColors;
  const commonStyles = createCommonStyles(isDarkMode);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [services, setServices] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [categories, setCategories] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  // Infinite scroll state za mobilne uređaje
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); // Default 5 za mobilne
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    tenantId: "",
    categoryId: undefined,
    duration: "",
    price: {
      amount: "",
    },
    isActive: true,
    maxAdvanceBooking: 30,
    minAdvanceBooking: 0,
  });

  console.log(
    "🔄 Services component render - page:",
    page,
    "rowsPerPage:",
    rowsPerPage,
    "selectedTenantId:",
    selectedTenantId
  );
  // console.log("Tenants", tenants);
  // console.log("Categories state:", categories);
  // console.log("SelectedTenantId:", selectedTenantId);
  // console.log("Pagination state - page:", page, "rowsPerPage:", rowsPerPage, "totalCount:", totalCount);
  // console.log("Services length:", services.length);

  const fetchTenants = useCallback(async () => {
    try {
      const isSuperAdmin = currentUser?.userType === "super_admin";
      const isTenantAdmin = isTenantAdminHelper(currentUser, selectedTenantId);
      const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId;

      if (isSuperAdmin) {
        const response = await tenantService.getAllTenants();
        setTenants(response.tenants || response);
      } else if (isTenantAdmin && tenantId) {
        // Ensure a normalized shape with _id/name when pushing currentUser.tenantId
        const t = currentUser.tenantId;
        setTenants([
          typeof t === "object" ? t : { _id: t, name: "Your tenant" },
        ]);
      } else {
        setTenants([]);
      }
    } catch (err) {
      console.error("Error fetching tenants:", err);
    }
  }, [currentUser, selectedTenantId]); // <-- added selectedTenantId

  const fetchCategories = useCallback(async () => {
    try {
      const isSuperAdmin = currentUser?.userType === "super_admin";
      const isTenantAdmin = isTenantAdminHelper(currentUser, selectedTenantId);
      const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId;

      console.log("fetchCategories called with:", {
        isSuperAdmin,
        isTenantAdmin,
        selectedTenantId,
        tenantId,
      });

      if (isSuperAdmin) {
        if (!selectedTenantId) {
          console.log(
            "No selectedTenantId for super_admin, setting categories to []"
          );
          setCategories([]);
          return;
        }
        console.log(
          "Fetching categories for super_admin with selectedTenantId:",
          selectedTenantId
        );
        const response = await categoryService.getCategoriesByTenant(
          selectedTenantId,
          1,
          100
        ); // Uzmi sve kategorije
        console.log("Categories response:", response);
        const data = response.data || response;
        const categoriesToSet = data.categories || data || [];
        console.log("Setting categories to:", categoriesToSet);
        setCategories(categoriesToSet);
      } else if (isTenantAdmin && tenantId) {
        console.log(
          "Fetching categories for tenant_admin with tenantId:",
          tenantId
        );
        const response = await categoryService.getCategoriesByTenant(
          tenantId,
          1,
          100
        ); // Uzmi sve kategorije
        console.log("Categories response:", response);
        const data = response.data || response;
        const categoriesToSet = data.categories || data || [];
        console.log("Setting categories to:", categoriesToSet);
        setCategories(categoriesToSet);
      } else {
        console.log("No tenant found, setting categories to []");
        setCategories([]);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCategories([]);
    }
  }, [currentUser, selectedTenantId]);

  useEffect(() => {
    setCategories([]);
  }, []);

  // Postavi selectedTenantId u window za Layout komponentu
  useEffect(() => {
    window.selectedTenantId = selectedTenantId;
  }, [selectedTenantId]);

  // Reset page when selectedTenantId changes
  useEffect(() => {
    setPage(0);
  }, [selectedTenantId]);

  // Početni učitavanje bez paginacije - bez loading state-a da se izbegne refresh
  useEffect(() => {
    if (selectedTenantId || currentUser?.userType !== "super_admin") {
      console.log(
        "⚡ Initial fetchServices triggered - selectedTenantId:",
        selectedTenantId
      );

      const fetchServicesSilently = async () => {
        try {
          const isSuperAdmin = currentUser?.userType === "super_admin";
          const isTenantAdmin = isTenantAdminHelper(
            currentUser,
            selectedTenantId
          );
          const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId;

          let response;
          if (isSuperAdmin) {
            if (!selectedTenantId) {
              setServices([]);
              setTotalCount(0);
              return;
            }
            // Za mobilne uređaje koristi infinite scroll
            const pageToUse = isMobile ? 1 : page + 1;
            const limitToUse = isMobile ? itemsPerPage : rowsPerPage;

            response = await serviceService.getServicesByTenant(
              selectedTenantId,
              pageToUse,
              limitToUse
            );
          } else if ((isTenantAdmin || tenantId) && tenantId) {
            // Za mobilne uređaje koristi infinite scroll
            const pageToUse = isMobile ? 1 : page + 1;
            const limitToUse = isMobile ? itemsPerPage : rowsPerPage;

            response = await serviceService.getServicesByTenant(
              tenantId,
              pageToUse,
              limitToUse
            );
          } else {
            setServices([]);
            setTotalCount(0);
            return;
          }

          const servicesToSet = response.services || response;
          setServices(servicesToSet);
          const total =
            parseInt(response.total) ||
            parseInt(response.totalCount) ||
            (response.services ? response.services.length : response.length);
          setTotalCount(total);

          // Za mobilne uređaje postavi infinite scroll state
          if (isMobile) {
            setCurrentPage(1);
            setHasMore(servicesToSet.length < total);
          } else {
            // Sinhronizuj page sa backend-om za desktop
            const backendPage = parseInt(response.currentPage) - 1;
            if (backendPage !== page && Math.abs(backendPage - page) > 0) {
              setPage(backendPage);
            }
          }
        } catch (err) {
          console.error("Error fetching services:", err);
          setServices([]);
          setTotalCount(0);
        }
      };

      fetchServicesSilently();
    }
  }, [
    selectedTenantId,
    currentUser,
    isMobile,
    itemsPerPage,
    page,
    rowsPerPage,
  ]); // <-- include currentUser object

  // Paginacija - pozovi fetchServicesSilently kada se promene page ili rowsPerPage
  useEffect(() => {
    console.log(
      "⚡ Pagination fetchServices triggered - page:",
      page,
      "rowsPerPage:",
      rowsPerPage
    );
    if (page > 0 || rowsPerPage !== 10) {
      // Pozovi samo ako nije početno stanje

      const fetchServicesSilently = async () => {
        try {
          const isSuperAdmin = currentUser?.userType === "super_admin";
          const isTenantAdmin = isTenantAdminHelper(
            currentUser,
            selectedTenantId
          );
          const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId;

          let response;
          if (isSuperAdmin) {
            if (!selectedTenantId) {
              setServices([]);
              setTotalCount(0);
              return;
            }
            // Za mobilne uređaje koristi infinite scroll
            const pageToUse = isMobile ? 1 : page + 1;
            const limitToUse = isMobile ? itemsPerPage : rowsPerPage;

            response = await serviceService.getServicesByTenant(
              selectedTenantId,
              pageToUse,
              limitToUse
            );
          } else if ((isTenantAdmin || tenantId) && tenantId) {
            // Za mobilne uređaje koristi infinite scroll
            const pageToUse = isMobile ? 1 : page + 1;
            const limitToUse = isMobile ? itemsPerPage : rowsPerPage;

            response = await serviceService.getServicesByTenant(
              tenantId,
              pageToUse,
              limitToUse
            );
          } else {
            setServices([]);
            setTotalCount(0);
            return;
          }

          const servicesToSet = response.services || response;
          setServices(servicesToSet);
          const total =
            parseInt(response.total) ||
            parseInt(response.totalCount) ||
            (response.services ? response.services.length : response.length);
          setTotalCount(total);

          // Za mobilne uređaje postavi infinite scroll state
          if (isMobile) {
            setCurrentPage(1);
            setHasMore(servicesToSet.length < total);
          } else {
            // Sinhronizuj page sa backend-om za desktop
            const backendPage = parseInt(response.currentPage) - 1;
            if (backendPage !== page && Math.abs(backendPage - page) > 0) {
              setPage(backendPage);
            }
          }
        } catch (err) {
          console.error("Error fetching services:", err);
          setServices([]);
          setTotalCount(0);
        }
      };

      fetchServicesSilently();
    }
  }, [
    page,
    rowsPerPage,
    isMobile,
    itemsPerPage,
    currentUser,
    selectedTenantId,
  ]); // <-- include currentUser object

  // Memoizuj services da se ne re-renderuje tabela
  const memoizedServices = useMemo(() => {
    console.log(
      "🧠 memoizedServices recalculated - services length:",
      services.length
    );
    return services;
  }, [services]);

  // Memoizuj paginaciju da se ne re-renderuje
  const memoizedPagination = useMemo(() => {
    console.log(
      "🧠 memoizedPagination recalculated - page:",
      page,
      "rowsPerPage:",
      rowsPerPage,
      "totalCount:",
      totalCount
    );
    return { page, rowsPerPage, totalCount };
  }, [page, rowsPerPage, totalCount]);

  useEffect(() => {
    // console.log("=== Initial effect called ===");
    fetchTenants();
    // Učitaj kategorije za tenant_admin kada se komponenta prvi put učita
    const isTenantAdmin = isTenantAdminHelper(currentUser, selectedTenantId);
    const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId;
    if (isTenantAdmin && tenantId) {
      console.log("Initial load: fetching categories for tenant_admin");
      // Pozovi fetchCategories bez setovanja loading state-a
      const fetchCategoriesSilently = async () => {
        try {
          const response = await categoryService.getCategoriesByTenant(
            tenantId,
            1,
            100
          ); // Uzmi sve kategorije

          const data = response.data || response;
          const categoriesToSet = data.categories || data || [];
          setCategories(Array.isArray(categoriesToSet) ? categoriesToSet : []);
        } catch (err) {
          console.error("Error fetching categories:", err);
          setCategories([]);
        }
      };

      fetchCategoriesSilently();
    }
  }, [currentUser, selectedTenantId, fetchTenants]); // <-- include currentUser

  // Infinite scroll funkcija za mobilne uređaje
  const loadMoreServices = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;

      const isSuperAdmin = currentUser?.userType === "super_admin";
      const isTenantAdmin = isTenantAdminHelper(currentUser, selectedTenantId);
      const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId;

      let response;
      if (isSuperAdmin) {
        response = await serviceService.getServicesByTenant(
          selectedTenantId,
          nextPage,
          itemsPerPage
        );
      } else if ((isTenantAdmin || tenantId) && tenantId) {
        response = await serviceService.getServicesByTenant(
          tenantId,
          nextPage,
          itemsPerPage
        );
      } else {
        return;
      }

      const data = response.data || response;
      const newServices = data.services || data;

      if (Array.isArray(newServices) && newServices.length > 0) {
        setServices((prev) => [...prev, ...newServices]);
        setCurrentPage(nextPage);

        // Proveri da li ima još podataka
        const total = parseInt(data.total) || parseInt(data.totalCount) || 0;
        const loadedCount = services.length + newServices.length;
        setHasMore(loadedCount < total);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error loading more services:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [
    currentUser,
    selectedTenantId,
    currentPage,
    itemsPerPage,
    loadingMore,
    hasMore,
    services.length,
  ]);

  // Scroll event listener za infinite scroll
  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 1000
      ) {
        loadMoreServices();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile, loadMoreServices]);

  const handleOpenDialog = useCallback(
    (service = null) => {
      if (service) {
        setEditingService(service);
        setFormData({
          name: service.name || "",
          description: service.description || "",
          tenantId: service.tenantId?._id || service.tenantId || "",
          categoryId: service.categoryId || undefined,
          duration: service.duration || "",
          price: {
            amount: service.price?.amount ?? service.price ?? "",
          },
          isActive: service.isActive !== undefined ? service.isActive : true,
          maxAdvanceBooking: service.maxAdvanceBooking || 30,
          minAdvanceBooking: service.minAdvanceBooking || 0,
        });
      } else {
        setEditingService(null);
        setFormData({
          name: "",
          description: "",
          tenantId: selectedTenantId,
          categoryId: undefined,
          duration: "",
          price: {
            amount: "",
          },
          isActive: true,
          maxAdvanceBooking: 30,
          minAdvanceBooking: 0,
        });
      }
      setOpenDialog(true);

      // Učitaj kategorije kada se otvori modal
      console.log("handleOpenDialog: calling fetchCategories");
      fetchCategories();
    },
    [selectedTenantId, fetchCategories]
  );

  const handleChangePage = (event, newPage) => {
    console.log(
      "📄 handleChangePage - changing page from",
      page,
      "to",
      newPage
    );
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    console.log(
      "📊 handleChangeRowsPerPage - changing rowsPerPage from",
      rowsPerPage,
      "to",
      event.target.value
    );
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingService(null);
    setFormData({
      name: "",
      description: "",
      tenantId: selectedTenantId,
      categoryId: undefined,
      duration: "",
      price: {
        amount: "",
      },
      isActive: true,
      maxAdvanceBooking: 30,
      minAdvanceBooking: 0,
    });
  };

  // Event listener za otvaranje dijaloga iz header dugmeta
  useEffect(() => {
    const handleOpenDialogEvent = () => {
      handleOpenDialog();
    };

    window.addEventListener("openServiceDialog", handleOpenDialogEvent);
    return () => {
      window.removeEventListener("openServiceDialog", handleOpenDialogEvent);
    };
  }, [handleOpenDialog]);

  const handleSubmit = async () => {
    if (submitting) return; // Prevent double submission
    setSubmitting(true);
    try {
      const isSuperAdmin = currentUser?.userType === "super_admin";
      const isTenantAdmin = isTenantAdminHelper(currentUser, selectedTenantId);
      const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId;

      if (!isSuperAdmin && !isTenantAdmin) {
        toast.error("Nemate dozvolu za kreiranje/uređivanje usluga");
        return;
      }

      // Validacija obaveznih polja
      if (!formData.name.trim()) {
        toast.error("Naziv usluge je obavezan");
        return;
      }

      if (!formData.duration) {
        toast.error("Trajanje usluge je obavezno");
        return;
      }

      if (!formData.price.amount || parseFloat(formData.price.amount) <= 0) {
        toast.error("Cena usluge mora biti veća od 0");
        return;
      }

      if (isSuperAdmin && !selectedTenantId) {
        toast.error("Morate izabrati klijenta iz dropdown-a");
        return;
      }

      if (
        !formData.categoryId &&
        Array.isArray(categories) &&
        categories.length > 0
      ) {
        toast.error("Morate izabrati kategoriju");
        return;
      }

      // Build payload WITHOUT mutating state
      const dataToSend = {
        ...formData,
        tenantId: isSuperAdmin ? selectedTenantId : tenantId,
        price: {
          amount: parseFloat(formData.price.amount) || 0,
        },
      };

      // Ukloni categoryId ako je prazan string
      if (dataToSend.categoryId === "") {
        delete dataToSend.categoryId;
      }

      // Dodaj tenantId ako nije super admin
      if (!isSuperAdmin && tenantId) {
        dataToSend.tenantId = tenantId;
      }

      if (!editingService) {
        dataToSend.createdBy = currentUser._id;
      } else {
        dataToSend.updatedBy = currentUser._id;
      }

      // Dodaj maxAdvanceBooking i minAdvanceBooking
      dataToSend.maxAdvanceBooking = parseInt(formData.maxAdvanceBooking) || 30;
      dataToSend.minAdvanceBooking = parseInt(formData.minAdvanceBooking) || 0;

      // Dodaj timestamp polja
      if (!editingService) {
        dataToSend.createdAt = new Date();
      }
      dataToSend.updatedAt = new Date();

      if (editingService) {
        await serviceService.updateService(editingService._id, dataToSend);
        toast.success("Usluga je uspešno ažurirana");
      } else {
        await serviceService.createService(dataToSend);
        toast.success("Usluga je uspešno kreirana");
      }
      handleCloseDialog();

      // Reload services silently without page refresh
      const fetchServicesSilently = async () => {
        try {
          const isSuperAdmin = currentUser?.userType === "super_admin";
          const isTenantAdmin = currentUser?.userType === "tenant_admin";
          const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId;

          let response;
          if (isSuperAdmin) {
            if (!selectedTenantId) {
              setServices([]);
              setTotalCount(0);
              return;
            }
            // Za mobilne uređaje koristi infinite scroll
            const pageToUse = isMobile ? 1 : page + 1;
            const limitToUse = isMobile ? itemsPerPage : rowsPerPage;

            response = await serviceService.getServicesByTenant(
              selectedTenantId,
              pageToUse,
              limitToUse
            );
          } else if ((isTenantAdmin || tenantId) && tenantId) {
            // Za mobilne uređaje koristi infinite scroll
            const pageToUse = isMobile ? 1 : page + 1;
            const limitToUse = isMobile ? itemsPerPage : rowsPerPage;

            response = await serviceService.getServicesByTenant(
              tenantId,
              pageToUse,
              limitToUse
            );
          } else {
            setServices([]);
            setTotalCount(0);
            return;
          }

          const servicesToSet = response.services || response;
          setServices(servicesToSet);
          const total =
            parseInt(response.total) ||
            parseInt(response.totalCount) ||
            (response.services ? response.services.length : response.length);
          setTotalCount(total);

          // Za mobilne uređaje postavi infinite scroll state
          if (isMobile) {
            setCurrentPage(1);
            setHasMore(servicesToSet.length < total);
          } else {
            // Sinhronizuj page sa backend-om za desktop
            const backendPage = parseInt(response.currentPage) - 1;
            if (backendPage !== page && Math.abs(backendPage - page) > 0) {
              setPage(backendPage);
            }
          }
        } catch (err) {
          console.error("Error reloading services:", err);
        }
      };

      fetchServicesSilently();
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Greška pri kreiranju/usluzi usluge"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Da li ste sigurni da želite da obrišete ovu uslugu?")) {
      try {
        const isSuperAdmin = currentUser?.userType === "super_admin";
        const isTenantAdmin = currentUser?.userType === "tenant_admin";
        const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId;

        if (!isSuperAdmin && !isTenantAdmin) {
          toast.error("Nemate dozvolu za brisanje usluga");
          return;
        }

        if (!isSuperAdmin && tenantId) {
          const serviceToDelete = services.find((s) => s._id === id);
          if (
            serviceToDelete?.tenantId?._id !== tenantId &&
            serviceToDelete?.tenantId !== tenantId
          ) {
            toast.error("Možete obrisati samo usluge iz vašeg tenant-a");
            return;
          }
        }

        await serviceService.deleteService(id);
        toast.success("Usluga je uspešno obrisana");

        // Reload services silently without page refresh
        const fetchServicesSilently = async () => {
          try {
            const isSuperAdmin = currentUser?.userType === "super_admin";
            const isTenantAdmin = currentUser?.userType === "tenant_admin";
            const tenantId =
              currentUser?.tenantId?._id || currentUser?.tenantId;

            let response;
            if (isSuperAdmin) {
              if (!selectedTenantId) {
                setServices([]);
                setTotalCount(0);
                return;
              }
              // Za mobilne uređaje koristi infinite scroll
              const pageToUse = isMobile ? 1 : page + 1;
              const limitToUse = isMobile ? itemsPerPage : rowsPerPage;

              response = await serviceService.getServicesByTenant(
                selectedTenantId,
                pageToUse,
                limitToUse
              );
            } else if ((isTenantAdmin || tenantId) && tenantId) {
              // Za mobilne uređaje koristi infinite scroll
              const pageToUse = isMobile ? 1 : page + 1;
              const limitToUse = isMobile ? itemsPerPage : rowsPerPage;

              response = await serviceService.getServicesByTenant(
                tenantId,
                pageToUse,
                limitToUse
              );
            } else {
              setServices([]);
              setTotalCount(0);
              return;
            }

            const servicesToSet = response.services || response;
            setServices(servicesToSet);
            const total =
              parseInt(response.total) ||
              parseInt(response.totalCount) ||
              (response.services ? response.services.length : response.length);
            setTotalCount(total);

            // Za mobilne uređaje postavi infinite scroll state
            if (isMobile) {
              setCurrentPage(1);
              setHasMore(servicesToSet.length < total);
            } else {
              // Sinhronizuj page sa backend-om za desktop
              const backendPage = parseInt(response.currentPage) - 1;
              if (backendPage !== page && Math.abs(backendPage - page) > 0) {
                setPage(backendPage);
              }
            }
          } catch (err) {
            console.error("Error reloading services:", err);
          }
        };

        fetchServicesSilently();
        handleCloseDialog();
      } catch (err) {
        toast.error(
          err.response?.data?.message ||
            err.message ||
            "Greška pri brisanju usluge"
        );
      }
    }
  };

  // const getTenantName = (tenantId) => {
  //   const id = tenantId?._id || tenantId;
  //   const tenant = tenants.find((t) => (t?._id || t) === id);
  //   return tenant?.name || "N/A";
  // };

  // const categories = [
  //   "beauty",
  //   "fitness",
  //   "health",
  //   "consulting",
  //   "education",
  //   "other",
  // ];
  // const currencies = ["RSD", "EUR", "USD"];

  return (
    <div>
      {/* Tenant dropdown za super admin */}
      {currentUser?.userType === "super_admin" && (
        <Box
          sx={{
            position: isMobile ? "sticky" : "static",
            top: isMobile ? "64px" : "auto", // AppBar visina
            zIndex: isMobile ? 1000 : "auto",
            backgroundColor: isMobile
              ? isDarkMode
                ? "rgba(30, 41, 59, 0.95)"
                : "rgba(255, 255, 255, 0.95)"
              : "transparent",
            backdropFilter: isMobile ? "blur(10px)" : "none",
            p: isMobile ? 2 : 0,
            mb: isMobile ? 2 : 0,
            borderBottom: isMobile
              ? `1px solid ${customColors.border.light}`
              : "none",
          }}
        >
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={commonStyles.textSecondary}>
              Izaberite klijenta
            </InputLabel>
            <Select
              value={selectedTenantId}
              label="Izaberite klijenta"
              onChange={(e) => setSelectedTenantId(e.target.value)}
              sx={commonStyles.selectField}
            >
              <MenuItem value="">
                <em>Izaberite klijenta...</em>
              </MenuItem>
              {tenants.map((tenant) => (
                <MenuItem key={tenant._id} value={tenant._id}>
                  {tenant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!selectedTenantId && (
            <Alert severity="info" sx={commonStyles.alert("info")}>
              Molimo izaberite klijenta da biste videli usluge.
            </Alert>
          )}
        </Box>
      )}

      {/* Add Service Button - samo za desktop */}
      {!isMobile && (
        <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-start" }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={commonStyles.gradientButton}
            disabled={
              currentUser?.userType === "super_admin" && !selectedTenantId
            }
          >
            Dodaj uslugu
          </Button>
        </Box>
      )}

      {/* Responsive prikaz - tabela za desktop, kartice za mobilne */}
      {isMobile ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {services.map((service, index) => (
            <Card
              key={service._id}
              sx={{
                background: isDarkMode
                  ? "rgba(30, 41, 59, 0.8)"
                  : "rgba(255, 255, 255, 1)",
                border: isDarkMode
                  ? "1px solid rgba(148, 163, 184, 0.1)"
                  : "1px solid rgba(0, 0, 0, 0.1)",
                borderRadius: 2,
                transition: "all 0.2s ease",
                boxShadow: isDarkMode
                  ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                  : "0 4px 12px rgba(0, 0, 0, 0.1)",
                "&:hover": {
                  background: isDarkMode
                    ? "rgba(30, 41, 59, 0.9)"
                    : "rgba(255, 255, 255, 1)",
                  border: isDarkMode
                    ? "1px solid rgba(148, 163, 184, 0.2)"
                    : "1px solid rgba(0, 0, 0, 0.2)",
                  boxShadow: isDarkMode
                    ? "0 8px 20px rgba(0, 0, 0, 0.4)"
                    : "0 8px 20px rgba(0, 0, 0, 0.15)",
                },
                mb: 1.5,
              }}
            >
              <CardContent sx={{ p: 1.5 }}>
                {/* Header sa nazivom i akcijama */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    mb: 1.5,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      ...commonStyles.textPrimary,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      flex: 1,
                    }}
                  >
                    {service.name}
                  </Typography>

                  {/* Akcije */}
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(service)}
                      sx={{
                        color: customColors.primary.main,
                        "&:hover": {
                          backgroundColor: `${customColors.primary.main}20`,
                        },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* Informacije u grid layout */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  {/* Kategorija */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        ...commonStyles.textSecondary,
                        fontSize: "0.75rem",
                      }}
                    >
                      Kategorija:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        ...commonStyles.textPrimary,
                        fontSize: "0.85rem",
                      }}
                    >
                      {(() => {
                        if (!Array.isArray(categories)) return "N/A";
                        // categoryId može biti objekat sa _id ili string
                        const categoryIdValue = typeof service.categoryId === 'object' 
                          ? service.categoryId?._id 
                          : service.categoryId;
                        const category = categories.find(
                          (cat) => cat._id === categoryIdValue
                        );
                        // Ako kategorija nije pronađena, pokušaj da koristiš name iz categoryId objekta
                        if (category) {
                          return category.name;
                        } else if (typeof service.categoryId === 'object' && service.categoryId?.name) {
                          return service.categoryId.name;
                        }
                        return "N/A";
                      })()}
                    </Typography>
                  </Box>

                  {/* Trajanje */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        ...commonStyles.textSecondary,
                        fontSize: "0.75rem",
                      }}
                    >
                      Trajanje:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        ...commonStyles.textPrimary,
                        fontSize: "0.85rem",
                      }}
                    >
                      {service.duration ? `${service.duration} min` : "N/A"}
                    </Typography>
                  </Box>

                  {/* Cena */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        ...commonStyles.textSecondary,
                        fontSize: "0.75rem",
                      }}
                    >
                      Cena:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        ...commonStyles.textPrimary,
                        fontSize: "0.85rem",
                      }}
                    >
                      {service.price?.amount
                        ? `${service.price.amount} din`
                        : "N/A"}
                    </Typography>
                  </Box>

                </Box>

                {/* Footer sa statusom i klijentom */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pt: 1,
                    borderTop: `1px solid ${customColors.border.light}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        ...commonStyles.textSecondary,
                        fontSize: "0.7rem",
                      }}
                    >
                      Status:
                    </Typography>
                    <Chip
                      label={service.isActive ? "Aktivan" : "Neaktivan"}
                      size="small"
                      color={service.isActive ? "success" : "default"}
                      sx={{
                        background: getStatusColors(service.isActive)
                          .background,
                        border: getStatusColors(service.isActive).border,
                        color: getStatusColors(service.isActive).light,
                        fontSize: "0.7rem",
                        height: 20,
                      }}
                    />
                  </Box>

                  {/* Klijent (samo za super_admin) */}
                  {currentUser?.userType === "super_admin" && (
                    <Typography
                      variant="caption"
                      sx={{
                        ...commonStyles.textSecondary,
                        fontSize: "0.7rem",
                      }}
                    >
                      {(() => {
                        const serviceTenantId =
                          typeof service.tenantId === "object"
                            ? service.tenantId._id
                            : service.tenantId;
                        const tenant = tenants.find(
                          (t) => t._id === serviceTenantId
                        );
                        return tenant ? tenant.name : "N/A";
                      })()}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}

          {/* Loading indicator za infinite scroll */}
          {loadingMore && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 3,
              }}
            >
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography variant="body2" sx={commonStyles.textSecondary}>
                Učitavanje dodatnih usluge...
              </Typography>
            </Box>
          )}

          {/* End of list indicator */}
          {!hasMore && services.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 2,
              }}
            >
              <Typography variant="body2" sx={commonStyles.textSecondary}>
                Prikazano je svih {services.length} usluga
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <div>
          {/* Desktop prikaz - tabela */}
          <TableContainer sx={{ backgroundColor: "transparent" }}>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    background: colors.table.header.background,
                    "& .MuiTableCell-head": {
                      color: commonStyles.textPrimary.color,
                      background:
                        getTableHeaderStyles(isDarkMode).cellBackground,
                      fontWeight: 600,
                      borderBottom: `1px solid ${customColors.border.light}`,
                    },
                  }}
                >
                  <TableCell>Naziv</TableCell>
                  {currentUser?.userType === "super_admin" && (
                    <TableCell>Klijent</TableCell>
                  )}
                  <TableCell>Kategorija</TableCell>
                  <TableCell>Trajanje</TableCell>
                  <TableCell>Cena</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Akcije</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {memoizedServices.map((service, index) => (
                  <TableRow
                    key={service._id}
                    sx={{
                      backgroundColor: getActualTableRowBackground(
                        index,
                        isDarkMode
                      ),
                      "& .MuiTableCell-body": {
                        borderBottom: `1px solid ${customColors.border.light}`,
                        color: commonStyles.textPrimary.color,
                      },
                    }}
                  >
                    <TableCell sx={commonStyles.textPrimary}>
                      <Box display="flex" alignItems="center">
                        <BuildIcon
                          sx={{ mr: 1, color: customColors.primary.main }}
                        />
                        {service.name}
                      </Box>
                    </TableCell>

                    {currentUser?.userType === "super_admin" && (
                      <TableCell sx={commonStyles.textPrimary}>
                        {(() => {
                          const serviceTenantId =
                            typeof service.tenantId === "object"
                              ? service.tenantId._id
                              : service.tenantId;
                          const tenant = tenants.find(
                            (t) => t._id === serviceTenantId
                          );
                          return tenant ? tenant.name : "N/A";
                        })()}
                      </TableCell>
                    )}

                    <TableCell sx={commonStyles.textPrimary}>
                      {(() => {
                        if (!Array.isArray(categories)) return "N/A";
                        // categoryId može biti objekat sa _id ili string
                        const categoryIdValue = typeof service.categoryId === 'object' 
                          ? service.categoryId?._id 
                          : service.categoryId;
                        const category = categories.find(
                          (cat) => cat._id === categoryIdValue
                        );
                        // Ako kategorija nije pronađena, pokušaj da koristiš name iz categoryId objekta
                        if (category) {
                          return category.name;
                        } else if (typeof service.categoryId === 'object' && service.categoryId?.name) {
                          return service.categoryId.name;
                        }
                        return "N/A";
                      })()}
                    </TableCell>

                    <TableCell sx={commonStyles.textPrimary}>
                      {service.duration ? `${service.duration} min` : "N/A"}
                    </TableCell>
                    <TableCell sx={commonStyles.textPrimary}>
                      {service.price?.amount
                        ? `${service.price.amount} din`
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={service.isActive ? "Aktivan" : "Neaktivan"}
                        size="small"
                        color={service.isActive ? "success" : "default"}
                        sx={{
                          background: getStatusColors(service.isActive)
                            .background,
                          border: getStatusColors(service.isActive).border,
                          color: getStatusColors(service.isActive).light,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(service)}
                        sx={commonStyles.iconButton(customColors.primary.main)}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginacija - samo za desktop */}
          {!isMobile && (
            <Pagination
              count={memoizedPagination.totalCount}
              page={memoizedPagination.page}
              onPageChange={handleChangePage}
              rowsPerPage={memoizedPagination.rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton
              showLastButton
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Redova po stranici:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} od ${count !== -1 ? count : `više od ${to}`}`
              }
            />
          )}
        </div>
      )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: commonStyles.dialogPaper,
        }}
      >
        <DialogTitle sx={commonStyles.dialogTitle}>
          {editingService ? "Uredi uslugu" : "Dodaj novu uslugu"}
        </DialogTitle>
        <DialogContent sx={{ ...commonStyles.dialogContent, pt: 2 }}>
          <Box sx={{ pt: 2 }}>
            {/* Prikaži koji tenant je odabran - na vrhu */}
            {currentUser?.userType === "super_admin" && selectedTenantId && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  ...commonStyles.glassContainer,
                  background: colors.status.error.background,
                }}
              >
                <Typography variant="body2" sx={commonStyles.textSecondary}>
                  Klijent:{" "}
                  <strong>
                    {tenants.find((t) => t._id === selectedTenantId)?.name}
                  </strong>
                </Typography>
              </Box>
            )}

            {/* Kategorija dropdown - na vrhu */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={commonStyles.textSecondary}>
                Kategorija
              </InputLabel>
              <Select
                value={formData.categoryId || ""}
                label="Kategorija"
                onChange={(e) =>
                  setFormData({ ...formData, categoryId: e.target.value })
                }
                sx={commonStyles.selectField}
              >
                <MenuItem value={undefined}>
                  <em>Izaberite kategoriju...</em>
                </MenuItem>
                {(() => {
                  return (
                    Array.isArray(categories) &&
                    categories.map((category) => (
                      <MenuItem key={category._id} value={category._id}>
                        {category.name}
                      </MenuItem>
                    ))
                  );
                })()}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Naziv usluge"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              sx={commonStyles.formField}
            />

            <TextField
              fullWidth
              label="Opis"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              sx={{ ...commonStyles.formField, mt: 3 }}
            />

            <Box
              display="grid"
              gridTemplateColumns="1fr 1fr"
              gap={2}
              mb={2}
              sx={{ mt: 3 }}
            >
              <FormControl fullWidth>
                <InputLabel sx={commonStyles.textSecondary}>
                  Trajanje (minuti)
                </InputLabel>
                <Select
                  value={formData.duration}
                  label="Trajanje (minuti)"
                  onChange={(e) =>
                    setFormData({ ...formData, duration: e.target.value })
                  }
                  sx={commonStyles.selectField}
                >
                  {[
                    15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180, 195,
                    210, 225, 240,
                  ].map((minutes) => (
                    <MenuItem key={minutes} value={minutes}>
                      {minutes} min
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Cena"
                type="number"
                value={formData.price.amount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    price: { ...formData.price, amount: e.target.value },
                  })
                }
                sx={commonStyles.formField}
              />
            </Box>

            <Box
              display="grid"
              gridTemplateColumns="1fr 1fr"
              gap={2}
              mb={2}
              sx={{ mt: 3 }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: customColors.primary.main,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: customColors.primary.main,
                        },
                    }}
                  />
                }
                label="Aktivan"
                sx={commonStyles.textPrimary}
              />
            </Box>

            {/* Dodatna podešavanja */}
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={commonStyles.textPrimary}
              >
                Podešavanja rezervacija
              </Typography>

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <TextField
                  fullWidth
                  label="Maksimalan broj dana unapred"
                  type="number"
                  value={formData.maxAdvanceBooking}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxAdvanceBooking: parseInt(e.target.value) || 0,
                    })
                  }
                  inputProps={{ min: 1, max: 365 }}
                  sx={commonStyles.formField}
                />
                <TextField
                  fullWidth
                  label="Minimalan broj dana unapred"
                  type="number"
                  value={formData.minAdvanceBooking}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      minAdvanceBooking: parseInt(e.target.value) || 0,
                    })
                  }
                  inputProps={{ min: 0, max: 30 }}
                  sx={commonStyles.formField}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            ...commonStyles.dialogActions,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Delete dugme - samo za editovanje - na levoj strani */}
          <Box>
            {editingService && (
              <Button
                onClick={() => handleDelete(editingService._id)}
                variant="outlined"
                startIcon={<DeleteIcon />}
                sx={{
                  color: colors.actions.delete,
                  borderColor: colors.actions.delete,
                  "&:hover": {
                    backgroundColor: `${colors.actions.delete}20`,
                    borderColor: colors.actions.delete,
                  },
                }}
              >
                Obriši
              </Button>
            )}
          </Box>

          {/* Ostala dugmad na desnoj strani */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={handleCloseDialog}
              variant="outlined"
              sx={commonStyles.cancelButton}
            >
              Otkaži
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={commonStyles.gradientButton}
              disabled={submitting}
            >
              {editingService ? "Ažuriraj" : "Kreiraj"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Services;
